// @todo: Migrate to 'Tooltip' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {OptionWithTooltip} from '@wandb/weave/common/components/elements/ModifiedDropdown';
import {ID} from '@wandb/weave/common/util/id';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useMemo} from 'react';
import {useHistory} from 'react-router-dom';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from 'react-toastify';

import {
  type Project,
  type useUserProjectsQuery,
  useViewDraftsQuery,
} from '../../generated/graphql';
import {FolderProjectIcon} from '../../pages/HomePage/HomePageContent/ActivitySection.styles';
import {useApolloClient} from '../../state/hooks';
import {useViewer} from '../../state/viewer/hooks';
import * as ViewApi from '../../state/views/api';
import {type ViewDraft} from '../../state/views/graphql';
import * as ViewNormalize from '../../state/views/normalize';
import {type ViewReducerState} from '../../state/views/reducerSupport';
import {type LoadableView} from '../../state/views/types';
import {getTeamsFromUserProjectsQuery} from '../CreateReportModal';
import {getDraftUrl} from './utils';

interface DropdownOption {
  text: string;
  value: string | number;
  label: React.JSX.Element;
}

const makeOption = (project: Partial<Project>): DropdownOption => {
  return {
    text: `${project.entityName}/${project.name}`,
    value: project.id ?? '',
    label: (
      <Tailwind>
        <div className="inline-flex gap-8">
          <FolderProjectIcon />
          <div className="flex max-w-[350px] flex-col">
            <OptionWithTooltip text={project?.name ?? ''} />
            <div className="flex text-moon-450">{project?.entityName}</div>
          </div>
        </div>
      </Tailwind>
    ),
  };
};

export function useProjectOptions(
  query: ReturnType<typeof useUserProjectsQuery>,
  currentProject?: Partial<Project>
) {
  return useMemo(() => {
    if (query.loading) {
      return [];
    }
    const teams = getTeamsFromUserProjectsQuery(query);

    const result: DropdownOption[] = [];
    if (currentProject) {
      result.push(makeOption(currentProject));
    }
    teams.forEach(t => {
      (t?.projects?.edges || []).forEach(({node}) => {
        if (node) {
          result.push(makeOption(node));
        }
      });
    });

    return result;
  }, [query, currentProject]);
}

export function useViewDraft({viewId}: {viewId: string}) {
  const viewer = useViewer();
  const history = useHistory();
  const viewDraftsQuery = useViewDraftsQuery({
    variables: {viewID: viewId},
    fetchPolicy: 'network-only',
  });

  const draft = useMemo(() => {
    if (viewDraftsQuery.data == null || viewer == null) {
      return undefined;
    }

    return (viewDraftsQuery.data.view?.children?.edges.find(
      e => e.node?.user?.id === viewer.id
    )?.node ?? undefined) as ViewDraft | undefined;
  }, [viewDraftsQuery.data, viewer]);

  return {
    draft,
    isViewDraftsQueryLoading: viewDraftsQuery.loading,
  };
}

export function useNavigateToNewDraft({
  view,
  views,
  entityName,
  projectName,
}: {
  view: LoadableView;
  views: ViewReducerState;
  entityName: string;
  projectName?: string;
}) {
  const history = useHistory();
  const client = useApolloClient();
  const navigateToNewDraft = useCallback(async () => {
    if (view.partRef == null) {
      return;
    }

    try {
      const newDraft = await ViewApi.save(client, {
        type: 'runs/draft',
        name: ID(12),
        displayName: view.displayName,
        description: view.description,
        spec: ViewNormalize.denormalize(views.parts, view.partRef, {
          includeRefs: false,
        }),
        project: view.project,
        parentId: view.id,
        previewUrl: view.previewUrl,
        coverUrl: view.coverUrl,
      });
      const draftUrl = getDraftUrl({
        parentId: view.id ?? '',
        draftId: newDraft.id ?? '',
        draftDisplayName: newDraft.displayName,
        entityName,
        projectName,
      });
      history.push(draftUrl);
    } catch (e) {
      toast('Error occurred. Please refresh and try again.');
      console.error(e);
    }
  }, [
    client,
    history,
    entityName,
    projectName,
    view.coverUrl,
    view.description,
    view.displayName,
    view.id,
    view.partRef,
    view.previewUrl,
    view.project,
    views.parts,
  ]);

  return navigateToNewDraft;
}
